// frontend/src/pages/MemberProfile.js
import React, { useState, useEffect, useCallback } from 'react';
import WebAuthnSetup from '../components/WebAuthnSetup';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Input,
  Button,
  Avatar,
  Text,
  Badge,
  Grid,
  GridItem,
  useToast,
  Divider,
  Switch,
  IconButton,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { FiEdit2, FiSave, FiX, FiCamera } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import { getPhotoUrl } from '../config/image';

export default function MemberProfile() {
  const { user, updateUser } = useAuth();
  const toast = useToast();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [bioModalOpen, setBioModalOpen] = useState(false);

  // Formater une date ISO en YYYY-MM-DD pour input type="date"
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await apiService.get('/members/me');
      
      console.log('📥 Profil chargé depuis API:', data);
      console.log('📅 date_naissance brute:', data.date_naissance);
      console.log('📅 date_naissance formatée:', formatDateForInput(data.date_naissance));
      console.log('📞 téléphone:', data.telephone);
      console.log('📷 photo:', data.photo);
      
      setProfile(data);
      setFormData({
        nom: data.nom || '',
        prenom: data.prenom || '',
        date_naissance: formatDateForInput(data.date_naissance),
        lieu_naissance: data.lieu_naissance || '',
        adresse_postale: data.adresse_postale || '',
        adresse: data.adresse || '',
        code_postal: data.code_postal || '',
        ville: data.ville || '',
        telephone: data.telephone || '',
        baptise: data.baptise || false,
        date_bapteme: data.date_bapteme ? data.date_bapteme.split('T')[0] : '',
        eglise_bapteme: data.eglise_bapteme || '',
        confirme: data.confirme || false,
        date_confirmation: data.date_confirmation ? data.date_confirmation.split('T')[0] : '',
        eglise_confirmation: data.eglise_confirmation || '',
      });
    } catch (error) {
      console.error('Erreur chargement profil:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger votre profil',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Fichier trop volumineux',
          description: 'La photo ne doit pas dépasser 5 Mo',
          status: 'error',
          duration: 5000,
        });
        return;
      }

      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);

      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          submitData.append(key, formData[key]);
        }
      });

      if (photoFile) {
        submitData.append('photo', photoFile);
      }

      // Ne PAS définir Content-Type manuellement pour FormData
      // Le navigateur ajoutera automatiquement le boundary
      await apiService.put('/members/me/profile', submitData);

      toast({
        title: 'Profil mis à jour',
        description: 'Vos informations ont été enregistrées avec succès',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setEditing(false);
      setPhotoFile(null);
      setPhotoPreview(null);
      await loadProfile();
      
      console.log('✅ Profil rechargé après sauvegarde');

    } catch (error) {
      console.error('Erreur mise à jour profil:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.error || 'Impossible de mettre à jour votre profil',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setPhotoFile(null);
    setPhotoPreview(null);
    // Réinitialiser le formulaire avec les données du profil
    setFormData({
      nom: profile.nom || '',
      prenom: profile.prenom || '',
      date_naissance: formatDateForInput(profile.date_naissance),
      lieu_naissance: profile.lieu_naissance || '',
      adresse_postale: profile.adresse_postale || '',
      adresse: profile.adresse || '',
      code_postal: profile.code_postal || '',
      ville: profile.ville || '',
      telephone: profile.telephone || '',
      baptise: profile.baptise || false,
      date_bapteme: profile.date_bapteme ? profile.date_bapteme.split('T')[0] : '',
      eglise_bapteme: profile.eglise_bapteme || '',
      confirme: profile.confirme || false,
      date_confirmation: profile.date_confirmation ? profile.date_confirmation.split('T')[0] : '',
      eglise_confirmation: profile.eglise_confirmation || '',
    });
  };

  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" color="green.500" />
      </Center>
    );
  }

  if (!profile) {
    return (
      <Container maxW="container.md" py={8}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Erreur</AlertTitle>
          <AlertDescription>Impossible de charger votre profil</AlertDescription>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* En-tête */}
        <HStack justify="space-between" align="center">
          <Heading size="lg" color="green.600">Mon Profil</Heading>
          {!editing ? (
            <Button
              leftIcon={<FiEdit2 />}
              colorScheme="green"
              onClick={() => setEditing(true)}
            >
              Modifier
            </Button>
          ) : (
            <HStack>
              <Button
                leftIcon={<FiX />}
                variant="ghost"
                onClick={handleCancel}
                isDisabled={saving}
              >
                Annuler
              </Button>
              <Button
                leftIcon={<FiSave />}
                colorScheme="green"
                onClick={handleSubmit}
                isLoading={saving}
                loadingText="Enregistrement..."
              >
                Enregistrer
              </Button>
            </HStack>
          )}
        </HStack>

        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={6}>
          {/* Carte Photo et Infos non modifiables */}
          <GridItem>
            <Card>
              <CardBody>
                <VStack spacing={4}>
                  <Box position="relative">
                    <Avatar
                      size="2xl"
                      src={photoPreview || getPhotoUrl(profile.photo)}
                      name={`${profile.prenom} ${profile.nom}`}
                    />
                    {editing && (
                      <IconButton
                        icon={<FiCamera />}
                        aria-label="Changer la photo"
                        position="absolute"
                        bottom={0}
                        right={0}
                        size="sm"
                        colorScheme="green"
                        borderRadius="full"
                        onClick={() => document.getElementById('photo-input').click()}
                      />
                    )}
                    <Input
                      id="photo-input"
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handlePhotoChange}
                    />
                  </Box>

                  <VStack spacing={2} w="100%">
                    <Text fontSize="xl" fontWeight="bold">
                      {profile.prenom} {profile.nom}
                    </Text>
                    <Badge colorScheme="green" fontSize="sm">
                      {profile.role || 'Membre'}
                    </Badge>
                  </VStack>

                  <Divider />

                  {/* Informations non modifiables */}
                  <VStack align="stretch" w="100%" spacing={3}>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                        Email
                      </Text>
                      <Text fontSize="sm">{profile.email || 'Non renseigné'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                        Matricule
                      </Text>
                      <Text fontSize="sm" fontFamily="mono">
                        {profile.matricule}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                        Numéro de carte
                      </Text>
                      <Text fontSize="sm" fontFamily="mono">
                        {profile.numero_carte}
                      </Text>
                    </Box>
                    {profile.groupes_noms && (
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                          Groupe(s)
                        </Text>
                        <Text fontSize="sm">{profile.groupes_noms}</Text>
                      </Box>
                    )}
                    {profile.fonction_nom && (
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                          Fonction
                        </Text>
                        <Text fontSize="sm">{profile.fonction_nom}</Text>
                      </Box>
                    )}
                  </VStack>
                </VStack>
              </CardBody>
            </Card>

            {/* Statistiques */}
            {profile.statistics && (
              <Card mt={4}>
                <CardHeader>
                  <Heading size="sm">Mes Contributions</Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={3}>
                    <Box>
                      <Text fontSize="xs" color="gray.500">Total payé</Text>
                      <Text fontSize="lg" fontWeight="bold" color="green.600">
                        {profile.statistics.montant_paye} €
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">Nombre de contributions</Text>
                      <Text fontSize="md">{profile.statistics.total_contributions}</Text>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
            )}
          </GridItem>

          {/* Formulaire d'édition */}
          <GridItem colSpan={{ base: 1, md: 2 }}>
            <Card>
              <CardHeader>
                <Heading size="md">Informations personnelles</Heading>
              </CardHeader>
              <CardBody>
                <form onSubmit={handleSubmit}>
                  <VStack spacing={4} align="stretch">
                    {/* Identité */}
                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                      <FormControl isRequired>
                        <FormLabel>Nom</FormLabel>
                        <Input
                          value={formData.nom}
                          onChange={(e) => handleInputChange('nom', e.target.value)}
                          isReadOnly={!editing}
                          bg={editing ? 'white' : 'gray.50'}
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>Prénom</FormLabel>
                        <Input
                          value={formData.prenom}
                          onChange={(e) => handleInputChange('prenom', e.target.value)}
                          isReadOnly={!editing}
                          bg={editing ? 'white' : 'gray.50'}
                        />
                      </FormControl>
                    </Grid>

                    {/* Naissance */}
                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                      <FormControl isRequired>
                        <FormLabel>Date de naissance</FormLabel>
                        <Input
                          type="date"
                          value={formData.date_naissance}
                          onChange={(e) => handleInputChange('date_naissance', e.target.value)}
                          isReadOnly={!editing}
                          bg={editing ? 'white' : 'gray.50'}
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>Lieu de naissance</FormLabel>
                        <Input
                          value={formData.lieu_naissance}
                          onChange={(e) => handleInputChange('lieu_naissance', e.target.value)}
                          isReadOnly={!editing}
                          bg={editing ? 'white' : 'gray.50'}
                        />
                      </FormControl>
                    </Grid>

                    <Divider my={2} />

                    {/* Contact */}
                    <FormControl>
                      <FormLabel>Téléphone</FormLabel>
                      <Input
                        type="tel"
                        value={formData.telephone}
                        onChange={(e) => handleInputChange('telephone', e.target.value)}
                        isReadOnly={!editing}
                        bg={editing ? 'white' : 'gray.50'}
                        placeholder="06 12 34 56 78"
                      />
                    </FormControl>

                    <Divider my={2} />

                    {/* Adresse */}
                    <FormControl>
                      <FormLabel>Adresse postale (complète)</FormLabel>
                      <Input
                        value={formData.adresse_postale}
                        onChange={(e) => handleInputChange('adresse_postale', e.target.value)}
                        isReadOnly={!editing}
                        bg={editing ? 'white' : 'gray.50'}
                        placeholder="Adresse complète"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Adresse (rue, numéro)</FormLabel>
                      <Input
                        value={formData.adresse}
                        onChange={(e) => handleInputChange('adresse', e.target.value)}
                        isReadOnly={!editing}
                        bg={editing ? 'white' : 'gray.50'}
                        placeholder="12 rue de la Paix"
                      />
                    </FormControl>

                    <Grid templateColumns={{ base: '1fr', md: '1fr 2fr' }} gap={4}>
                      <FormControl>
                        <FormLabel>Code postal</FormLabel>
                        <Input
                          value={formData.code_postal}
                          onChange={(e) => handleInputChange('code_postal', e.target.value)}
                          isReadOnly={!editing}
                          bg={editing ? 'white' : 'gray.50'}
                          placeholder="75001"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Ville</FormLabel>
                        <Input
                          value={formData.ville}
                          onChange={(e) => handleInputChange('ville', e.target.value)}
                          isReadOnly={!editing}
                          bg={editing ? 'white' : 'gray.50'}
                          placeholder="Paris"
                        />
                      </FormControl>
                    </Grid>

                    <Divider my={2} />

                    {/* Informations religieuses */}
                    <Text fontWeight="600" fontSize="sm" color="gray.600">Informations religieuses</Text>

                    {/* Baptême */}
                    <HStack spacing={4} align="center">
                      <FormControl display="flex" alignItems="center" w="auto">
                        <FormLabel mb={0} minW="80px">Baptisé(e)</FormLabel>
                        <Switch
                          isChecked={formData.baptise}
                          onChange={(e) => handleInputChange('baptise', e.target.checked)}
                          isDisabled={!editing}
                          colorScheme="green"
                        />
                      </FormControl>
                    </HStack>

                    {formData.baptise && (
                      <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={3}>
                        <FormControl>
                          <FormLabel fontSize="sm">Date du baptême</FormLabel>
                          <Input
                            type="date"
                            value={formData.date_bapteme}
                            onChange={(e) => handleInputChange('date_bapteme', e.target.value)}
                            isReadOnly={!editing}
                            bg={editing ? 'white' : 'gray.50'}
                            size="sm"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="sm">Église du baptême</FormLabel>
                          <Input
                            value={formData.eglise_bapteme}
                            onChange={(e) => handleInputChange('eglise_bapteme', e.target.value)}
                            isReadOnly={!editing}
                            bg={editing ? 'white' : 'gray.50'}
                            placeholder="Nom de l'église"
                            size="sm"
                          />
                        </FormControl>
                      </Grid>
                    )}

                    {/* Confirmation */}
                    <HStack spacing={4} align="center">
                      <FormControl display="flex" alignItems="center" w="auto">
                        <FormLabel mb={0} minW="80px">Confirmé(e)</FormLabel>
                        <Switch
                          isChecked={formData.confirme}
                          onChange={(e) => handleInputChange('confirme', e.target.checked)}
                          isDisabled={!editing}
                          colorScheme="green"
                        />
                      </FormControl>
                    </HStack>

                    {formData.confirme && (
                      <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={3}>
                        <FormControl>
                          <FormLabel fontSize="sm">Date de confirmation</FormLabel>
                          <Input
                            type="date"
                            value={formData.date_confirmation}
                            onChange={(e) => handleInputChange('date_confirmation', e.target.value)}
                            isReadOnly={!editing}
                            bg={editing ? 'white' : 'gray.50'}
                            size="sm"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="sm">Église de confirmation</FormLabel>
                          <Input
                            value={formData.eglise_confirmation}
                            onChange={(e) => handleInputChange('eglise_confirmation', e.target.value)}
                            isReadOnly={!editing}
                            bg={editing ? 'white' : 'gray.50'}
                            placeholder="Nom de l'église"
                            size="sm"
                          />
                        </FormControl>
                      </Grid>
                    )}
                  </VStack>
                </form>
              </CardBody>
            </Card>
          </GridItem>
        </Grid>

        {/* Section biométrie */}
        {typeof window !== 'undefined' && window.PublicKeyCredential && (
          <Card w="full">
            <CardHeader pb={0}>
              <Heading size="sm" color="gray.600">🔐 Connexion biométrique</Heading>
            </CardHeader>
            <CardBody>
              <Text fontSize="sm" color="gray.500" mb={3}>
                Activez l'empreinte digitale ou Face ID pour vous connecter sans mot de passe.
              </Text>
              <Button colorScheme="green" variant="outline" size="sm" onClick={() => setBioModalOpen(true)}>
                Gérer la biométrie
              </Button>
            </CardBody>
          </Card>
        )}

        <WebAuthnSetup isOpen={bioModalOpen} onClose={() => setBioModalOpen(false)} />
      </VStack>
    </Container>
  );
}